import React, { useState, useEffect } from "react";
import {
  Modal,
  FormActions,
  HorizontalDatePicker,
  TimeSlotSelector,
} from "../../shared";
import { appointmentsApi, publicApi, availabilityApi } from "../../../services";
import { Appointment, Company, TimeSlot } from "../../../types";
import { useCompany } from "../../../contexts/CompanyContext";
import { isBirthdayToday } from "../../../utils/dateUtils";

interface RescheduleAppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  appointment: Appointment;
  disablePast?: boolean;
  company?: Company | null; // Optional prop to override context
}

export const RescheduleAppointmentModal: React.FC<
  RescheduleAppointmentModalProps
> = ({ isOpen, onClose, onSuccess, appointment, company: propCompany, disablePast = false }) => {
  const { company: contextCompany } = useCompany();
  const company = propCompany ?? contextCompany;
  const [newDate, setNewDate] = useState(appointment.appointmentDate);
  const [newTime, setNewTime] = useState(appointment.startTime);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlot | null>(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [slotsError, setSlotsError] = useState<string | null>(null);

  // Fetch available slots when date changes (for non-car companies)
  useEffect(() => {
    if (
      company?.category !== "car" &&
      newDate &&
      appointment.professionalId &&
      appointment.serviceId &&
      isOpen
    ) {
      const fetchSlots = async () => {
        setLoadingSlots(true);
        setSlotsError(null);
        setSelectedTimeSlot(null);
        setTimeSlots([]);

        try {
          // Use publicApi for customer context, availabilityApi for admin context
          const response = propCompany
            ? await publicApi.getAvailableSlotsByProfessional(
                appointment.professionalId,
                appointment.serviceId,
                newDate
              )
            : await availabilityApi.getAvailableSlotsByProfessional(
                appointment.professionalId,
                appointment.serviceId,
                newDate
              );

          if (response.success && response.data) {
            setTimeSlots(response.data);
            // If the current time matches a slot, select it
            const matchingSlot = response.data.find(
              (slot: TimeSlot) => slot.startTime === appointment.startTime
            );
            if (matchingSlot) {
              setSelectedTimeSlot(matchingSlot);
              setNewTime(matchingSlot.startTime || "");
            }
          } else {
            setSlotsError(response.error || "Erro ao carregar horários");
          }
        } catch (err) {
          console.error("Error fetching slots:", err);
          setSlotsError("Erro ao carregar horários disponíveis");
        } finally {
          setLoadingSlots(false);
        }
      };

      fetchSlots();
    } else if (company?.category === "car") {
      // Reset slots for car companies
      setTimeSlots([]);
      setSelectedTimeSlot(null);
    }
  }, [
    newDate,
    company?.category,
    appointment.professionalId,
    appointment.serviceId,
    appointment.startTime,
    isOpen,
    propCompany,
  ]);

  // Update newTime when a slot is selected
  useEffect(() => {
    if (selectedTimeSlot?.startTime) {
      setNewTime(selectedTimeSlot.startTime);
    }
  }, [selectedTimeSlot]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setNewDate(appointment.appointmentDate);
      setNewTime(appointment.startTime);
      setTimeSlots([]);
      setSelectedTimeSlot(null);
      setError(null);
      setSlotsError(null);
    }
  }, [isOpen, appointment.appointmentDate, appointment.startTime]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // For non-car companies, require a selected time slot
    if (company?.category !== "car" && !selectedTimeSlot?.startTime) {
      setError("Selecione um horário disponível");
      return;
    }

    if (company?.category === "car" && !newDate) {
      setError("Preencha todos os campos");
      return;
    }

    // Check if date/time actually changed
    const timeChanged =
      company?.category === "car"
        ? false
        : selectedTimeSlot?.startTime !== appointment.startTime;
    if (newDate === appointment.appointmentDate && !timeChanged) {
      setError(
        company?.category === "car"
          ? "Selecione uma nova data"
          : "Selecione uma nova data/horário"
      );
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Use publicApi when company prop is provided (customer context), otherwise use appointmentsApi (admin context)
      const api = propCompany ? publicApi : appointmentsApi;
      const timeToUse = company?.category === "car" ? newTime : selectedTimeSlot?.startTime;
      const response = await api.rescheduleAppointment(
        appointment.id,
        newDate,
        company?.category === "car" ? undefined : timeToUse
      );

      if (response.success) {
        onSuccess();
      } else {
        setError(response.error || "Erro ao reagendar");
      }
    } catch (err) {
      console.error("Error rescheduling appointment:", err);
      setError("Erro ao reagendar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Reagendar Agendamento">
      <div className="space-y-6">
        {/* Current appointment info */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-2">Agendamento Atual:</h4>
          <div className="text-sm text-gray-600 space-y-1">
            <p className="flex items-center gap-1.5">
              <strong>Cliente:</strong> {appointment.client?.name}
              {isBirthdayToday((appointment.client as any)?.birthday) && (
                <span className="text-yellow-500 text-base" title="Aniversário hoje!">
                  🎂
                </span>
              )}
            </p>
            <p>
              <strong>Serviço:</strong> {appointment.service?.name}
            </p>
            {appointment.professionalName && (
              <p>
                <strong>Profissional:</strong> {appointment.professionalName}
              </p>
            )}
            <p>
              <strong>Data:</strong>{" "}
              {new Date(appointment.appointmentDate).toLocaleDateString(
                "pt-BR"
              )}
            </p>
            {company?.category !== "car" && (
              <p>
                <strong>Horário:</strong> {appointment.startTime}
              </p>
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          <div
            className={`grid gap-4 grid-cols-1`}
          >
            {/* New Date */}
            <div>
              <HorizontalDatePicker
                showBorder={false}
                selectedDate={newDate}
                disablePast={disablePast}
                onDateChange={setNewDate}
              />
            </div>
          </div>

          {/* Time Slots - Show for non-car companies */}
          {company?.category !== "car" && newDate && (
            <TimeSlotSelector
              timeSlots={timeSlots}
              selectedTimeSlot={selectedTimeSlot}
              onTimeSlotSelect={(slot) => {
                setSelectedTimeSlot(slot);
                if (error) setError(null);
              }}
              loading={loadingSlots}
              error={slotsError}
              label=""
            />
          )}

          {/* Form Actions */}
          <FormActions
            onCancel={onClose}
            onSubmit={() => {}}
            loading={loading}
            submitText="Reagendar"
            submitType="submit"
            isEditing={true}
          />
        </form>
      </div>
    </Modal>
  );
};
